import React from "react";
import { getSensorValues, formatSensorValue } from "../../../utils/valueEngine";

/* ─── Group config ─── */
const GROUP_CONFIG = {
  inlet:   { label: "Inlet",       icon: "↓", color: "text-blue-600",   bg: "bg-blue-50",   border: "border-blue-100"  },
  outlet:  { label: "Outlet",      icon: "↑", color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100"},
  level:   { label: "Water Level", icon: "≈", color: "text-cyan-600",   bg: "bg-cyan-50",   border: "border-cyan-100"  },
  others:  { label: "Others",      icon: "◈", color: "text-gray-600",   bg: "bg-gray-50",   border: "border-gray-100"  },
};
const GROUP_ORDER = ["inlet", "outlet", "level", "others"];
GROUP_CONFIG.ambient = {
  label: "Ambient",
  icon: "AMB",
  color: "text-teal-600",
  bg: "bg-teal-50",
  border: "border-teal-100",
};
GROUP_ORDER.splice(3, 0, "ambient");

const normalizeGroup = (groupType) => {
  const group = String(groupType || "others").trim().toLowerCase();
  if (["general", "other", "others", "misc", "miscellaneous"].includes(group)) {
    return "others";
  }
  return group || "others";
};

/* ─── Value color logic ─── */
const getValueColor = (value) => {
  if (value == null)  return { text: "text-gray-300", bg: "bg-gray-50",    dot: "bg-gray-300"  };
  if (value > 200)    return { text: "text-red-600",  bg: "bg-red-50",     dot: "bg-red-500"   };
  if (value > 100)    return { text: "text-amber-600",bg: "bg-amber-50",   dot: "bg-amber-500" };
  return               { text: "text-emerald-600",    bg: "bg-emerald-50", dot: "bg-emerald-500"};
};

/* ════════════════════════════════════════
   SENSOR CARD
════════════════════════════════════════ */
const SensorCard = ({ id, label, value, unit, display }) => {
  const hasValue = value != null;
  const colors   = getValueColor(hasValue ? value : null);

  return (
    <div className="group relative bg-white border border-gray-100 rounded-xl p-4
                    hover:border-gray-200 hover:shadow-sm transition-all duration-150">

      {/* Top row */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold text-gray-400 tracking-widest font-mono uppercase">
          {id}
        </span>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot} ${hasValue ? "animate-pulse" : ""}`} />
      </div>

      {/* Value */}
      <div className={`text-2xl font-extrabold tracking-tight leading-none mb-2 ${colors.text}`}>
        {hasValue ? display : <span className="text-gray-200 text-xl">—</span>}
      </div>

      {/* Label */}
      <p className="text-[11px] text-gray-400 font-medium truncate" title={label}>
        {label}
      </p>

      {/* Unit pill */}
      {unit && hasValue && (
        <span className={`absolute top-3 right-3 hidden group-hover:inline-block
                          text-[9px] font-semibold px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
          {unit}
        </span>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   GROUP SECTION
════════════════════════════════════════ */
const GroupSection = ({ group, sensors }) => {
  const cfg = GROUP_CONFIG[group] || GROUP_CONFIG.others;
  return (
    <div>
      {/* Group header */}
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg ${cfg.bg} ${cfg.border} border mb-4`}>
        <span className={`text-xs font-bold ${cfg.color}`}>{cfg.icon}</span>
        <span className={`text-[11px] font-bold uppercase tracking-widest ${cfg.color}`}>
          {cfg.label}
        </span>
        <span className={`text-[10px] font-semibold ${cfg.color} opacity-60`}>
          {sensors.length}
        </span>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {sensors.map((s) => (
          <SensorCard key={s.id} {...s} />
        ))}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════ */
function SensorsTab({ liveData, sensors = [] }) {

  /* ── No live data yet ── */
  if (!liveData || Object.keys(liveData).length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-12 flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-[3px] border-gray-100 border-t-blue-400 animate-spin" />
        <p className="text-[13px] text-gray-400 font-medium">Waiting for live data…</p>
        <p className="text-[11px] text-gray-300">Data will appear once the device starts transmitting</p>
      </div>
    );
  }

  /* ── No sensors configured ── */
  if (sensors.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-12 text-center">
        <p className="text-[13px] text-gray-400 font-medium">No sensors configured for this product</p>
      </div>
    );
  }

  /* ── Build + group sensors ── */
  const sensorValues = getSensorValues(sensors, liveData);
  const allSensors = sensors
    .map((s) => {
      const value = sensorValues[String(s.key || "").toLowerCase()] ?? null;
      return {
        id:         s.key?.toUpperCase(),
        label:      s.label || s.key,
        unit:       s.unit  || "",
        order:      s.id    || 0,
        value,
        display:    formatSensorValue(s, value),
        group_type: normalizeGroup(s.group_type),
      };
    })
    .sort((a, b) => a.order - b.order);

  const grouped = allSensors.reduce((acc, s) => {
    if (!acc[s.group_type]) acc[s.group_type] = [];
    acc[s.group_type].push(s);
    return acc;
  }, {});

  const renderGroups = [
    ...GROUP_ORDER,
    ...Object.keys(grouped)
      .filter((group) => !GROUP_ORDER.includes(group))
      .sort(),
  ];

  /* ── Active sensor count ── */
  const activeCount = allSensors.filter((s) => s.value != null).length;

  return (
    <div className="space-y-6">

      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-[13px] font-semibold text-gray-700">Sensor Readings</h2>
          <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {allSensors.length} sensors
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[11px] text-emerald-600 font-medium">
            {activeCount} active
          </span>
        </div>
      </div>

      {/* Group sections */}
      <div className="space-y-7">
        {renderGroups.map((group) => {
          const list = grouped[group];
          if (!list?.length) return null;
          return <GroupSection key={group} group={group} sensors={list} />;
        })}
      </div>

    </div>
  );
}

export default SensorsTab;
